import React, { useRef } from "react";
import MaInput from "../../../../Components/UI/ma-input";
import gallery from "../../../../assets/Icons/dashboard/gallery.svg";
import { IoClose } from "react-icons/io5";

const MAX_IMAGES = 5;
const MAX_VIDEOS = 2;

export function AddProductStepMedia({
  imageItems,
  onAddImages,
  onRemoveImage,
  videoItems,
  onAddVideos,
  onRemoveVideo,
  embedVideoUrls,
  onEmbedVideoUrlsChange,
}) {
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const handleImagePick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    onAddImages?.(files, MAX_IMAGES);
  };

  const handleVideoPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    onAddVideos?.(files, MAX_VIDEOS);
  };

  return (
    <div className="flex flex-col gap-5 sm:gap-6">
      <div
        style={{ borderBottom: "1px solid #EAEAEA" }}
        className="px-4 pb-3 pt-4 sm:px-5 md:px-8 md:pt-0"
      >
        <h2 className="my-0 text-[16px] font-[900] text-[#1F2937] sm:text-[18px]">
          Upload Photos and Video
        </h2>
        <p className="my-0 mt-1 text-[12px] text-[#9D9FA4] sm:text-[13px]">
          High-quality visuals dramatically increase conversions.
        </p>
      </div>

      <input
        ref={imageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        multiple
        className="hidden"
        onChange={handleImagePick}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/x-msvideo"
        multiple
        className="hidden"
        onChange={handleVideoPick}
      />

      <div className="px-4 sm:px-5 md:px-8">
        <p className="my-0 mb-2 text-[12px] font-semibold text-[#374151] sm:text-[13px]">
          📸 Product Images{" "}
          <span className="font-normal text-[#6B7280]">
            (First image is the cover photo)
          </span>
        </p>
        <button
          type="button"
          onClick={() => imageInputRef.current?.click()}
          className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#D1D5DB] bg-[#FAFAFA] px-3 py-8 text-center transition-colors hover:border-[#9CA3AF] hover:bg-[#F3F4F6] sm:px-4 sm:py-10"
        >
          <img src={gallery} alt="" className="h-9 w-9 opacity-80 sm:h-10 sm:w-10" />
          <span className="text-[11px] font-bold text-[#3D3D3D] sm:text-[12px]">
            Drop images here or click to browse
          </span>
          <span className="text-[10px] text-[#626262]">
            JPEG, PNG — Max 5MB each. Max {MAX_IMAGES} images
          </span>
        </button>

        {imageItems?.length > 0 ? (
          <div className="mt-4 flex flex-wrap gap-3">
            {imageItems.map((item, index) => (
              <div
                key={item.id}
                className="relative h-24 w-24 overflow-hidden rounded-lg border border-[#E5E7EB] bg-[#F3F4F6]"
              >
                <img
                  src={item.preview}
                  alt=""
                  className="h-full w-full object-cover"
                />
                {index === 0 ? (
                  <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">
                    Cover
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() => onRemoveImage?.(item.id)}
                  className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-[#DC2626] shadow-sm hover:bg-white"
                  aria-label="Remove image"
                >
                  <IoClose className="text-lg" />
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="px-4 pb-1 sm:px-5 md:px-8">
        <p className="my-0 mb-2 text-[12px] font-bold text-[#3D3D3D]">
          🎬 Product Videos{" "}
          <span className="font-normal text-[#626262]">
            (Optional – up to {MAX_VIDEOS} videos, max 100 MB each)
          </span>
        </p>
        <button
          type="button"
          onClick={() => videoInputRef.current?.click()}
          className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#D1D5DB] bg-[#FAFAFA] px-3 py-8 text-center transition-colors hover:border-[#9CA3AF] hover:bg-[#F3F4F6] sm:px-4 sm:py-10"
        >
          <img src={gallery} alt="" className="h-9 w-9 opacity-80 sm:h-10 sm:w-10" />
          <span className="text-[11px] font-bold text-[#3D3D3D] sm:text-[12px]">
            Drop video here or click to browse
          </span>
          <span className="text-[10px] text-[#626262]">
            MP4, MOV, AVI — Max 100MB each. Max {MAX_VIDEOS} videos
          </span>
        </button>

        {videoItems?.length > 0 ? (
          <ul className="mt-3 space-y-2 text-[12px] text-[#374151]">
            {videoItems.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-2 rounded-lg border border-[#E5E7EB] bg-[#FAFAFA] px-3 py-2"
              >
                <span className="truncate font-medium">{item.file.name}</span>
                <button
                  type="button"
                  onClick={() => onRemoveVideo?.(item.id)}
                  className="shrink-0 text-[#DC2626]"
                  aria-label="Remove video"
                >
                  <IoClose className="text-xl" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="px-4 pb-4 sm:px-5 md:px-8 md:pb-6">
        <MaInput
          label="Or embed a video URL"
          fullWidth
          placeholder="Enter URL"
          borderColor="#DADADA"
          value={embedVideoUrls}
          onChange={(e) => onEmbedVideoUrlsChange?.(e.target.value)}
        />
        <p className="mt-1 text-[12px] text-[#9CA3AF]">
          youtube, instagram etc
        </p>
      </div>
    </div>
  );
}
